import { useState, useEffect, useCallback } from 'react'
import { api } from '../../api/client'

// Real backing store for the Lead and Deal detail modals' Activity/Comments
// feed, using the existing generic GoodDayComment system already wired up
// in apps/api/src/routes/goodday/collaboration.ts (GET/POST /comments) -
// previously local React state seeded with hardcoded fake entries and a
// hardcoded fake author regardless of who was actually logged in.

export interface Comment {
  id: string
  author: string
  authorId: string
  avatarUrl?: string
  text: string
  createdAt: string
}

function fromCommentRow(row: any): Comment {
  const user = typeof row.userId === 'object' && row.userId ? row.userId : null
  return {
    id: row._id ?? row.id,
    author: user?.name || 'Unknown',
    authorId: user?._id ?? row.userId,
    avatarUrl: user?.avatarUrl,
    text: row.content,
    createdAt: row.createdAt,
  }
}

export function useComments(entityType: 'lead' | 'deal', entityId: string | undefined) {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)

  const fetchComments = useCallback(async () => {
    if (!entityId) {
      setComments([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>('/comments', { entityType, entityId })
      setComments((res?.data || []).map(fromCommentRow))
    } catch (err) {
      console.error(`Failed to load ${entityType} comments`, err)
      setComments([])
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const createComment = useCallback(
    async (text: string) => {
      if (!entityId) throw new Error(`${entityType}Id is required`)
      const res = await api.post<any>(
        '/comments',
        { content: text, entityType, entityId },
        { skipErrorToast: true }
      )
      const created = fromCommentRow(res?.data)
      setComments((prev) => [...prev, created])
      return created
    },
    [entityType, entityId]
  )

  return { comments, loading, createComment, refetch: fetchComments }
}

export function useLeadComments(leadId: string | undefined) {
  return useComments('lead', leadId)
}

export function useDealComments(dealId: string | undefined) {
  return useComments('deal', dealId)
}
